(() => {
  if (customElements.get('ho-modular-calculator')) return;
  const frame = () => new Promise(resolve => requestAnimationFrame(resolve));
  const positive = value => Number.isFinite(value) && value > 0;
  const ceilCount = value => Math.max(1, Math.ceil(value - 16 * Number.EPSILON * Math.max(1, value)));
  const widthCm = value => {
    const match = String(value || '').trim().match(/^(\d+(?:[.,]\d+)?)\s*(?:[x×]\s*\d+(?:[.,]\d+)?)?\s*(cm|m)$/i);
    return match ? Number(match[1].replace(',', '.')) * (match[2].toLowerCase() === 'm' ? 100 : 1) : null;
  };
  const numericOrder = (a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0) || a.localeCompare(b, undefined, { numeric: true });
  let locks = 0, inheritedLock = false;
  function hedgePanel(x, y, width, height) {
    const centre=x+width/2, base=y+height, branch=width*.29;
    return `<ellipse cx="${centre}" cy="${base+1}" rx="${width*.52}" ry="2.2" fill="#5c8c28"/><rect x="${x}" y="${y}" width="${width}" height="${height}" rx="2.4" fill="#71a62e" stroke="#596442" stroke-width="1.6"/><path d="M${x+4} ${y+height*.31}V${y+7}Q${x+4} ${y+4} ${x+8} ${y+4}H${x+width*.55}" fill="none" stroke="#96bd37" stroke-width="3.5" stroke-linecap="round"/><path d="M${centre} ${base+1}V${y+height*.42}M${centre} ${y+height*.66}L${centre-branch} ${y+height*.51}M${centre} ${y+height*.77}L${centre+branch} ${y+height*.61}M${centre} ${y+height*.88}L${centre-branch} ${y+height*.73}M${centre} ${y+height*.53}L${centre+branch*.8} ${y+height*.37}" fill="none" stroke="#7c613e" stroke-width="${Math.max(1.5,width*.08)}" stroke-linecap="round" stroke-linejoin="round"/>`;
  }
  function icon(width) {
    const canopy = Math.min(34, Math.max(16, width / 40 * 19.2));
    return `<svg viewBox="0 0 64 64" aria-hidden="true" focusable="false"><circle cx="32" cy="32" r="30.5" fill="#fff" stroke="#aacb84" stroke-width=".8"/>${hedgePanel(32-canopy/2,12,canopy,44)}</svg>`;
  }
  function unitPanel(x, y, width, height) {
    const base = y + height;
    return `<ellipse cx="${x+width/2}" cy="${base+3}" rx="${width*.52}" ry="2" fill="#bdd19f"/><rect x="${x}" y="${y}" width="${width}" height="${height}" rx="1.8" fill="#71a62e" stroke="#596442" stroke-width="1.6"/><path d="M${x+4} ${y+height-5}V${y+4}H${x+width-4}" fill="none" stroke="#96bd37" stroke-width="3"/><path d="M${x+width*.2} ${base-5}v8m${width*.3} -8v8m${width*.3} -8v8" fill="none" stroke="#7c613e" stroke-width="1.4"/>`;
  }
  class HarrodModularCalculator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      requestAnimationFrame(() => { if (this.isConnected) this.init(); });
    }
    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.unlock();
      this.abort?.abort();
      if (this.dialog?.open) this.dialog.close();
      if (this.dialog) this.append(this.dialog);
      this.ready = false;
    }
    node(name) { return this.dialog?.querySelector(`[data-mc-${name}]`); }
    text(name, value) { const node = this.node(name); if (node) node.textContent = value; }
    t(key, values = {}) { return String(this.strings?.[key] || '').replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? '')); }
    number(value) { return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { maximumFractionDigits: 2 }).format(value); }
    init() {
      this.dialog = this.querySelector('[data-mc-dialog]');
      this.form = this.closest('form[data-product-form]');
      try {
        const data = JSON.parse(this.querySelector('[data-mc-data]').textContent);
        this.options = data.options || [];
        this.variants = (data.variants || []).map(item => ({ ...item, id: String(item.id) }));
        this.strings = data.strings || {};
        this.kind = this.dataset.kind;
        this.widthIndex = this.options.findIndex(option => /width/i.test(option.name));
        this.heightIndex = this.options.findIndex(option => /height/i.test(option.name));
        this.variant = this.variants.find(item => item.id === String(data.selected)) || this.variants.find(item => item.available);
        this.length = '';
        if (!this.dialog || !this.form || !this.variants.length) throw new Error('Missing calculator data');
        document.body.append(this.dialog);
        const options = { signal: this.abort.signal };
        const open = this.querySelector('[data-mc-open]');
        open.disabled = false;
        open.addEventListener('click', () => this.open(open), options);
        this.node('close').addEventListener('click', () => this.dialog.close(), options);
        this.node('length').addEventListener('input', event => { this.length = event.target.value; this.error = ''; this.render(); }, options);
        this.node('options').addEventListener('click', event => this.choose(event), options);
        this.node('apply').addEventListener('click', () => this.apply(), options);
        this.dialog.addEventListener('close', () => {
          this.generation = (this.generation || 0) + 1;
          this.unlock();
          if (this.isConnected) this.opener?.focus({ preventScroll: true });
        }, options);
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        }, options);
        this.render();
      } catch (_) {
        this.querySelector('[data-mc-open]')?.setAttribute('disabled', '');
      }
    }
    nativeSelection() { return this.form.querySelector('variant-selection'); }
    nativeQuantity() { return this.form.querySelector('quantity-selector input[name="quantity"]') || this.form.querySelector('input[name="quantity"]'); }
    lock() {
      if (this.locked) return;
      if (!locks) inheritedLock = document.documentElement.classList.contains('ho-mc-open');
      locks += 1; this.locked = true;
      document.documentElement.classList.add('ho-mc-open');
    }
    unlock() {
      if (!this.locked) return;
      locks = Math.max(0, locks - 1); this.locked = false;
      if (!locks && !inheritedLock) document.documentElement.classList.remove('ho-mc-open');
    }
    open(button) {
      if (this.applying) return;
      this.generation = (this.generation || 0) + 1;
      this.opener = button;
      const selected = this.nativeSelection()?.getAttribute('variant') || this.form.querySelector('[name="id"]')?.value;
      this.variant = this.variants.find(item => item.id === String(selected)) || this.variant;
      this.error = '';
      this.render();
      this.dialog.showModal();
      this.lock();
      requestAnimationFrame(() => this.node('options').querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true }));
    }
    unit(count) { return this.t(this.kind === 'units' ? count === 1 ? 'unitOne' : 'unitMany' : count === 1 ? 'elementOne' : 'elementMany'); }
    width(variant = this.variant) { return this.kind === 'units' ? 100 : widthCm(variant?.options[this.widthIndex]); }
    validLength() { return String(this.length).trim() !== '' && positive(Number(this.length)) && Number(this.length) <= 10000; }
    calculate() {
      const width = this.width(), variant = this.variant;
      if (!variant?.available || !positive(width) || !this.validLength()) return null;
      const count = ceilCount(Number(this.length) * 100 / width);
      const minimum = Number(variant.min) || 1, step = Number(variant.step) || 1;
      const maximum = variant.max === null || variant.max === undefined || variant.max === '' ? Infinity : Number(variant.max);
      if (!Number.isSafeInteger(count) || !Number.isSafeInteger(minimum) || !Number.isSafeInteger(step) || minimum < 1 || step < 1) return null;
      const order = minimum + Math.ceil(Math.max(0, count - minimum) / step) * step;
      if (order > maximum) return { error: this.t('maxError', { max: this.number(maximum) }) };
      return { count, order, minimum, step, width, coverage: count * width / 100, extra: Math.max(0, count * width / 100 - Number(this.length)) };
    }
    candidates(index, value) {
      return this.variants.filter(variant => variant.available && variant.options[index] === value && variant.options.slice(0, index).every((value, i) => value === this.variant?.options[i]));
    }
    choose(event) {
      const button = event.target.closest('[data-mc-option]');
      if (!button || button.disabled || this.applying) return;
      const index = Number(button.dataset.mcOption), value = button.dataset.value;
      const choices = this.candidates(index, value);
      this.variant = choices.find(candidate => candidate.options.every((value, i) => i === index || value === this.variant?.options[i])) || choices[0] || this.variant;
      this.error = '';
      this.render();
      this.node('options').querySelector(`[data-mc-option="${index}"][data-value="${CSS.escape(value)}"]`)?.focus({ preventScroll: true });
    }
    renderOptions() {
      const host = this.node('options'); host.replaceChildren();
      this.options.forEach((option, index) => {
        if (option.name === 'Title' && option.values.length === 1 && option.values[0] === 'Default Title') return;
        const label = document.createElement('p'); label.className = 'ho-mc-field-label'; label.id = this.dialog.id + '-option-' + index; label.textContent = option.name;
        const group = document.createElement('div'); group.className = index === this.widthIndex && this.kind === 'elements' ? 'ho-mc-width-choices' : 'ho-mc-choices'; group.setAttribute('role', 'group'); group.setAttribute('aria-labelledby', label.id);
        [...option.values].sort(numericOrder).forEach(value => {
          const available = this.candidates(index, value).length > 0;
          const button = document.createElement('button'); button.type = 'button'; button.className = 'ho-mc-choice'; button.dataset.mcOption = index; button.dataset.value = value; button.disabled = !available || Boolean(this.applying); button.setAttribute('aria-pressed', String(value === this.variant?.options[index]));
          if (index === this.widthIndex && this.kind === 'elements') {
            button.classList.add('ho-mc-width-choice'); button.innerHTML = icon(widthCm(value) || 40);
            const copy = document.createElement('span'), title = document.createElement('strong'), sub = document.createElement('small');
            title.textContent = value.replace(/\s*x\s*/gi, ' × ');
            sub.textContent = !available ? this.t('unavailable') : positive(widthCm(value)) ? this.t('covers', { width: this.number(widthCm(value) / 100) }) : this.t('noWidth');
            copy.append(title, sub); button.append(copy);
          } else button.textContent = value;
          if (!available) button.title = this.t('unavailable');
          group.append(button);
        });
        host.append(label, group);
      });
      const unitCard = this.node('unit-card'); unitCard.hidden = this.kind !== 'units';
      if (this.kind === 'units' && !unitCard.childElementCount) {
        unitCard.innerHTML = '<img src="https://harrod-outdoors.myshopify.com/cdn/shop/files/198-hedging-styles-hedging-unit.png?v=1790159404&width=80" alt="" width="54" height="52">';
        const copy = document.createElement('span'), title = document.createElement('strong'), sub = document.createElement('small'); title.textContent = this.t('unitWidth'); sub.textContent = this.t('unitRate'); copy.append(title, sub); unitCard.append(copy);
      }
      this.text('size-help', this.kind === 'units' ? this.t('unitHelp') : this.t('widthHelp'));
    }
    diagram(result) {
      const width = this.width() || 40, total = result?.count || 8, count = Math.min(total, 30);
      const visualWidth = this.kind === 'units' ? 78 : Math.min(70, width * .75);
      const heightValue = widthCm(this.variant?.options[this.heightIndex]);
      const height = this.kind === 'units' ? 52 : Math.min(88, Math.max(50, (heightValue || 125) * .6));
      const canvas = Math.max(450, count * visualWidth + 60), start = (canvas - count * visualWidth) / 2, bottom = 102;
      const panel = this.kind === 'units' ? unitPanel : hedgePanel;
      const panels = Array.from({ length: count }, (_, index) => panel(start + index * visualWidth, bottom - height, visualWidth, height)).join('');
      this.node('diagram').innerHTML = `<svg viewBox="0 0 ${canvas} 128" aria-hidden="true" focusable="false"><path d="M${start-13} 106H${canvas-start+13}" stroke="#bfd29f"/>${panels}<path d="M${start} 117H${canvas-start}M${start} 113V121M${canvas-start} 113V121" stroke="#7c613e" stroke-width=".8"/></svg>`;
    }
    render() {
      this.renderOptions();
      this.node('length').disabled = Boolean(this.applying);
      const result = this.calculate(), complete = result && !result.error;
      const error = this.error || result?.error || (!this.variant?.available ? this.t('unavailable') : !positive(this.width()) ? this.t('noWidth') : this.length !== '' && !this.validLength() ? this.t('lengthError') : '');
      this.text('error', error); this.node('error').hidden = !error;
      this.text('width', positive(this.width()) ? this.t('widthEach', { width: this.number(this.width()), unit: this.unit(1) }) : '');
      this.text('count', complete ? this.number(result.count) : '—');
      this.text('count-label', this.t('needed', { unit: this.unit(complete ? result.count : 2) }));
      this.text('apply', this.applying ? this.t('applying') : complete ? this.t('useCount', { count: this.number(result.order), unit: this.unit(result.order) }) : !this.variant?.available ? this.t('chooseSize') : this.t('enterLength'));
      this.node('apply').disabled = !complete || Boolean(this.applying);
      this.text('coverage', complete ? this.t('coverage', { count: this.number(result.count), unit: this.unit(result.count), coverage: this.number(result.coverage) }) : this.t('enterLength'));
      const note = complete ? result.order > result.count ? result.count < result.minimum ? this.t('minimumNote', { minimum: this.number(result.minimum) }) : this.t('incrementNote', { order: this.number(result.order), step: this.number(result.step) }) : result.extra > .000001 ? this.t('extra', { extra: this.number(result.extra) }) : this.t('layoutHelp') : this.t('layoutHelp');
      this.text('note', complete && result.count > 30 ? note + ' ' + this.t('sample') : note);
      this.diagram(complete ? result : null);
    }
    async apply() {
      const result = this.calculate(), variant = this.variant;
      if (!result || result.error || this.applying) return;
      const generation = this.generation;
      const current = () => this.isConnected && this.dialog.open && this.generation === generation;
      this.applying = true; this.error = ''; this.render();
      try {
        const selection = this.nativeSelection(), options = selection?.querySelector('options-selection');
        if (!selection || !this.nativeQuantity()) throw new Error('Missing native controls');
        await customElements.whenDefined('variant-selection');
        if (!current()) throw new Error('Cancelled');
        if (options) {
          await customElements.whenDefined('options-selection');
          if (!current()) throw new Error('Cancelled');
          if (typeof options.setSelectedOptions !== 'function' || !await options.setSelectedOptions(variant.options)) throw new Error('Could not select size');
        } else selection.variant = variant.id;
        if (String(selection.getAttribute('variant')) !== variant.id) throw new Error('Wrong selected variant');
        if (typeof selection.getVariant === 'function') {
          const selected = await selection.getVariant();
          if (String(selected?.id) !== variant.id || selected.available === false) throw new Error('Unavailable variant');
        }
        await frame(); await frame();
        if (!current()) throw new Error('Cancelled');
        const id = this.form.querySelector('[data-variants][name="id"]'), input = this.nativeQuantity();
        if (String(id?.value) !== variant.id || input.disabled) throw new Error('Native selection mismatch');
        const previous = input.value;
        input.value = String(result.order);
        if (!input.checkValidity()) { input.value = previous; throw new Error('Quantity is invalid'); }
        input.dispatchEvent(new Event('input', { bubbles: true })); input.dispatchEvent(new Event('change', { bubbles: true }));
        await frame();
        if (String(id.value) !== variant.id || Number(input.value) !== result.order) throw new Error('Quantity changed');
        this.dialog.close();
        requestAnimationFrame(() => { input.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' }); input.focus({ preventScroll: true }); });
      } catch (_) { if (current()) this.error = this.t('applyError'); }
      finally { this.applying = false; if (this.isConnected) this.render(); }
    }
  }
  customElements.define('ho-modular-calculator', HarrodModularCalculator);
})();
